import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import {
  NextActionDetails,
  NextActionsDto,
  ResponsiblePartyEnum
} from '@bo-client/shared/models/next-actions.dto';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { uri } from '@bo-client/shared/constants/uri';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { combineLatestWith, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NextActionsService {
  constructor(private httpHapClient: HttpHapService, private authorizationService: AuthorizationService) {
    this.loadNextActions$().subscribe((nextActions) => this._nextActions$.next(nextActions));

    this.currentResponsibleParty$ = this.authorizationService.isEditor$.pipe(
      combineLatestWith(this.authorizationService.isInstaller$),
      map(([isEditor, isInstaller]) => {
        if (isEditor) {
          return ResponsiblePartyEnum.GridOperator;
        } else if (isInstaller) {
          return ResponsiblePartyEnum.Installer;
        } else {
          return ResponsiblePartyEnum.None;
        }
      })
    );
  }

  private _nextActions$: BehaviorSubject<NextActionsDto[]> = new BehaviorSubject([]);
  nextActions$: Observable<NextActionsDto[]> = this._nextActions$.asObservable();
  currentResponsibleParty$: Observable<ResponsiblePartyEnum>;

  getActionDetails$(actionType: string, entityId?: number): Observable<NextActionDetails> {
    return this.nextActions$.pipe(
      map((actions) => actions.find((a) => a.nextActionType === actionType)),
      switchMap((action) => {
        if (action) {
          return this.isResponsible$(action.responsibleParty).pipe(
            map((isResponsible) => {
              const responsibleOrNone =
                isResponsible || action.responsibleParty === ResponsiblePartyEnum.None;
              return {
                title: responsibleOrNone ? action.title : 'Keine Aktion erforderlich',
                description: responsibleOrNone
                  ? action.description || ''
                  : this.defaultDescriptions[action.responsibleParty],
                mailDescription: responsibleOrNone
                  ? action.mailDescription || ''
                  : this.defaultDescriptions[action.responsibleParty],
                isResponsible,
                actionUrl: isResponsible && entityId ? this.getActionUrl(actionType, entityId) : null,
                icon: responsibleOrNone ? action.icon || null : 'info_outlined'
              };
            })
          );
        } else {
          return of({
            title: 'Aktion nicht gefunden',
            description: `Die zugewiesene Aktion '${actionType}'${
              entityId ? ` für Id ${entityId}` : ''
            } konnte nicht gefunden werden.`,
            mailDescription: '',
            isResponsible: false,
            actionUrl: null,
            icon: 'error_outline'
          });
        }
      })
    );
  }

  // Private Methoden und Properties

  private defaultDescriptions = {
    [ResponsiblePartyEnum.Installer]: 'Der Antrag befindet sich in der Bearbeitung durch einen Installateur.',
    [ResponsiblePartyEnum.GridOperator]: 'Der Antrag wird derzeit vom Netzbetreiber geprüft.',
    [ResponsiblePartyEnum.Customer]: 'Der Antrag wird aktuell vom Kunden bearbeitet.',
    [ResponsiblePartyEnum.None]: 'Für diesen Antrag sind keine weiteren Aktionen erforderlich.'
  };

  /* TODO: Methode implementieren
   *   Die URL dient als Shortcut, um die Seite aufzurufen, in der man die Aktion abschließen kann.
   *   Der Rückgabewert könnte auch ein Observable sein
   *   Eine Möglichkeit wäre, alle ActionUrls in einer Konstanten zu speichern, ähnlich wie bei den ActionWithMoreInfo Typen
   * */
  private getActionUrl(actionName: string, id: number) {
    return null;
  }

  private isResponsible$(responsibleParty: ResponsiblePartyEnum): Observable<boolean> {
    // Für leichteres Testen sind Superadmins immer verantwortlich
    return this.authorizationService.hasRole$('ROLE_SUPERADMIN').pipe(
      switchMap((isSuperadmin) => {
        if (isSuperadmin && responsibleParty !== ResponsiblePartyEnum.None) {
          return of(true);
        }
        switch (responsibleParty) {
          case ResponsiblePartyEnum.Installer: {
            return this.authorizationService.isInstaller$;
          }
          case ResponsiblePartyEnum.GridOperator: {
            return this.authorizationService.isEditor$;
          }
          // Momentan gibt es keine Rollen für "Customer" oder "None"
          default: {
            return of(false);
          }
        }
      })
    );
  }

  private loadNextActions$() {
    return this.httpHapClient.get(uri.nextActions + '/') as Observable<NextActionsDto[]>;
  }
}
